import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import { GoApplicationButtonsComponent } from '../go-application-buttons/go-application-buttons.component';
import { takeUntil, filter, tap } from 'rxjs';
import { NavigationText } from '../../../../../../core/constants/navigation-text';
import { ApplicationState } from '../../../../../../core/models/application-state/application-state';
import { NavigationOptions } from '../../../../../../shared/components/page-header-nav/models/navigation-options';
import { PageHeaderNavItem } from '../../../../../../shared/components/page-header-nav/models/page-header-nav-item';
import { StepLink } from '../../../../shared/models/step-link';
import { StepNavigationButtonVisibilityService } from '../../../../shared/services/step-navigation-button-visibility/step-navigation-button-visibility.service';
import { StepNavigationButtonService } from '../../../../shared/services/step-navigation-button/step-navigation-button.service';
import { StepNavigationService } from '../../../../shared/services/step-navigation/step-navigation.service';
import { GoSearchInformationService } from '../../../go-frequency-results/services/go-search-information-form/go-search-information.service';
import { NavigationLinkDefinitions } from '../../../../shared/constants/navigation-link-definitions';
import { MatDialog } from '@angular/material/dialog';
import { GoApplicationPageNavigationBaseComponent } from '../go-application-page-navigation-base/go-application-page-navigation-base.component';
import { GoSubmitToEwaFormService } from '../../../go-frequency-results/services/go-submit-to-ewa-form/go-submit-to-ewa-form.service';

@Component({
    selector: 'app-go-application-coordination-page-navigation',
    imports: [GoApplicationButtonsComponent],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './go-application-coordination-page-navigation.component.html',
})
export class GoApplicationCoordinationPageNavigationComponent
    extends GoApplicationPageNavigationBaseComponent
    implements OnInit, OnDestroy
{
    navItems: NavigationOptions = {};
    applicationState: ApplicationState | null = null;

    isConfirmOrderButtonDisabled = false;

    title: string | undefined;

    private resultsExpired = false;

    private lastStepLink: StepLink | undefined;

    constructor(
        protected override readonly stepNavigationService: StepNavigationService,
        protected override readonly stepNavigationButtonService: StepNavigationButtonService,
        protected override readonly stepNavigationOptionsService: StepNavigationButtonVisibilityService,
        protected override readonly dialog: MatDialog,
        private readonly goSearchInformationService: GoSearchInformationService,
        private readonly goSubmitToEwaFormService: GoSubmitToEwaFormService,
    ) {
        super(
            stepNavigationService,
            stepNavigationButtonService,
            stepNavigationOptionsService,
            dialog,
        );
    }

    override ngOnInit() {
        super.ngOnInit();

        this.handleGoConfirmOrderButtonDisabledStateIfApplicable();
    }

    override ngOnDestroy() {
        super.ngOnDestroy();
    }

    protected subscribeToStepChanges(): void {
        this.stepNavigationService.currentStep$
            .pipe(
                takeUntil(this.destroy$),
                filter(
                    (currentStep): currentStep is StepLink =>
                        currentStep !== undefined,
                ),
                tap((currentStep: StepLink) => {
                    this.setNavigationLinks(currentStep);
                    this.lastStepLink = currentStep;
                }),
            )
            .subscribe();
    }

    private setNavigationLinks(stepLink: StepLink) {
        if (this.shouldHandleResultsExpired(stepLink)) {
            this.updateResultsExpiredHandler();
        }

        this.setPrimaryLinks(stepLink);
        this.setSecondaryLinks(stepLink);
    }

    private updateResultsExpiredHandler(): void {
        this.goSearchInformationService
            .onResultsExpired(() => (this.resultsExpired = true))
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.navItems.primaries = [];

                    if (this.lastStepLink) {
                        this.setPrimaryLinks(this.lastStepLink);
                    }
                }),
            )
            .subscribe();
    }

    private shouldHandleResultsExpired(stepLink: StepLink): boolean {
        return this.stepNavigationOptionsService.needsGoConfirmOrderButton(
            stepLink,
        );
    }

    private setPrimaryLinks(stepLink: StepLink) {
        const primaryLinks: PageHeaderNavItem[] = [];

        if (this.needsCoordinationReRunButton(stepLink)) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.GO_RE_RUN_COORDINATION,
                    this.next.bind(this),
                ),
            );
        } else if (
            this.stepNavigationOptionsService.needsGoConfirmOrderButton(
                stepLink,
            )
        ) {
            primaryLinks.push(
                this.stepNavigationButtonService.createPrimaryLink(
                    NavigationText.GO_CONFIRM_ORDER,
                    this.next.bind(this),
                    undefined,
                    this.isConfirmOrderButtonDisabled,
                ),
            );
        }

        this.navItems.primaries = primaryLinks;
    }

    private setSecondaryLinks(stepLink: StepLink) {
        const secondaryLinks: PageHeaderNavItem[] = [];

        if (
            this.stepNavigationOptionsService.needsCallEWAForAssistanceButton(
                stepLink,
            )
        ) {
            secondaryLinks.push(
                this.stepNavigationButtonService.createCustomCssLink(
                    NavigationText.CONTACT_EWA_FOR_ASSISTANCE,
                    NavigationLinkDefinitions.CallEwaForAssistanceLinkCss,
                    this.openContactForAssistanceModal.bind(this),
                    NavigationLinkDefinitions.BluePhoneImageUrl,
                ),
            );
        }

        this.navItems.secondaries = secondaryLinks;
    }

    private needsCoordinationReRunButton(stepLink: StepLink): boolean {
        return (
            this.stepNavigationOptionsService.needsGoConfirmOrderButton(
                stepLink,
            ) && this.resultsExpired
        );
    }

    private handleGoConfirmOrderButtonDisabledStateIfApplicable(): void {
        // Re-evaluate whenever the function reference changes
        this.goSubmitToEwaFormService.isFormInvalid$
            .pipe(takeUntil(this.destroy$))
            .subscribe((fn) => {
                this.isConfirmOrderButtonDisabled = fn();

                if (this.lastStepLink) {
                    this.setPrimaryLinks(this.lastStepLink);
                }
            });
    }
}
